import React from "react";
import { Box, Typography, Stack } from "@mui/material";
import { format, subDays, startOfDay } from "date-fns";
import { DateTime } from "luxon";
import { HabitClass, HabitClassHabitEntriesItem } from "../../generated/model";

interface HabitCalendarWeekViewProps {
  habit: HabitClass;
  habitEntries: HabitClassHabitEntriesItem[];
  onDayClick: (date: string, habitId: string) => void;
}

const HabitCalendarWeekView: React.FC<HabitCalendarWeekViewProps> = ({
  habit,
  habitEntries,
  onDayClick,
}) => {
  // Generate the last 7 days
  const today = startOfDay(new Date());
  const last7Days = Array.from({ length: 7 }, (_, index) => {
    return subDays(today, 6 - index); // Start from 6 days ago to today
  });

  // Get habit entries for this specific habit
  const habitSpecificEntries = habitEntries.filter(
    (entry) => entry.habitId === habit.id
  );

  // Check if a day has an entry
  const hasEntry = (date: Date): boolean => {
    const dateString = format(date, "yyyy-MM-dd");
    return habitSpecificEntries.some(
      (entry) => entry.date === dateString && entry.completed
    );
  };

  // Handle cell click
  const handleCellClick = (date: Date) => {
    const dateString = format(date, "yyyy-MM-dd");
    onDayClick(dateString, habit.id);
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Habit Name */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
        {habit.name}
      </Typography>

      {/* Week View */}
      <Stack direction="row" spacing={1} justifyContent="center">
        {last7Days.map((date, index) => {
          const dayAbbreviation = format(date, "EEE"); // Mon, Tue, Wed, etc.
          const dayNumber = format(date, "d");
          const hasHabitEntry = hasEntry(date);
          const isToday =
            format(date, "yyyy-MM-dd") === format(today, "yyyy-MM-dd");

          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: 40,
              }}
            >
              {/* Day abbreviation */}
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  mb: 0.5,
                  fontWeight: isToday ? 600 : 400,
                }}
              >
                {dayAbbreviation}
              </Typography>

              {/* Day cell */}
              <Box
                onClick={() => handleCellClick(date)}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  border: "2px solid",
                  borderColor: isToday ? "primary.main" : "grey.300",
                  backgroundColor: hasHabitEntry
                    ? "primary.main"
                    : "transparent",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: hasHabitEntry
                      ? "primary.dark"
                      : "grey.100",
                    borderColor: isToday ? "primary.dark" : "grey.400",
                  },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.75rem",
                    color: hasHabitEntry ? "white" : "text.primary",
                    fontWeight: isToday ? 600 : 400,
                  }}
                >
                  {dayNumber}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>

      {/* Optional: Progress indicator */}
      <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
        <Typography variant="caption" color="text.secondary">
          {(() => {
            const completedDays = habitSpecificEntries.filter(
              (entry) => entry.completed
            ).length;
            const habitCreatedAt = DateTime.fromISO(habit.createdAt);
            const today = DateTime.now();
            const daysSinceCreated =
              Math.floor(today.diff(habitCreatedAt, "days").days) + 1; // +1 to include the creation day

            return `${completedDays} / ${daysSinceCreated} days completed`;
          })()}
        </Typography>
      </Box>
    </Box>
  );
};

export default HabitCalendarWeekView;
